import React, { useState, useEffect } from 'react';
import type { OnlinePackage, PackagesResponse } from '@fittrack/services';
import '../styles/PackageBoxes.css';

interface PackageBoxesProps {
  companyId: string;
  siteId: number;
  apiUrl?: string;
  // Optional: scope the list to only packages that grant access to this calendar
  // (e.g. the member clicked a class slot before they had any package). Leave
  // unset for the normal "show every online package" behavior.
  calendarId?: number;
  onPackageSelect: (packageId: number) => void;
  onCancel?: () => void;
  // Called once, before falling back to the unfiltered list, if calendarId was
  // set but no package currently grants access to it (e.g. the package tied to
  // the member's originally-clicked slot was deactivated). Lets the caller know
  // the slot-specific context no longer applies to whatever gets picked next.
  onFilteredEmpty?: () => void;
  /** JWT of the already-authenticated caller (e.g. a logged-in PWA member).
   * When present, sent as a Bearer token. Omit for the anonymous guest
   * flow (FitTrackOnline). */
  authToken?: string;
}

const PackageBoxes: React.FC<PackageBoxesProps> = ({ companyId, siteId, apiUrl = '', calendarId, onPackageSelect, onCancel, onFilteredEmpty, authToken }) => {
  const [packages, setPackages] = useState<OnlinePackage[]>([]);
  const [packageCss, setPackageCss] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPackages();
  }, [companyId, siteId, calendarId]);

  const fetchOnlineBoxes = async (scopeToCalendarId?: number): Promise<PackagesResponse> => {
    const base = apiUrl ? `${apiUrl}/api/package/onlineboxes/${siteId}` : `/api/package/onlineboxes/${siteId}`;
    const url = scopeToCalendarId != null ? `${base}?calendarId=${scopeToCalendarId}` : base;
    const response = await fetch(url, {
      headers: {
        'X-Tenant-ID': companyId,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch packages');
    }

    return response.json();
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      let data = await fetchOnlineBoxes(calendarId);

      // The calendar-scoped package (e.g. the one tied to a class the member
      // tried to book) may have since been deactivated — fall back to showing
      // every package rather than a dead end, and tell the caller this happened
      // so it can drop the now-invalid slot-specific context downstream.
      if (calendarId != null && (data.packages || []).length === 0) {
        onFilteredEmpty?.();
        data = await fetchOnlineBoxes(undefined);
      }

      setPackages(data.packages || []);
      setPackageCss(data.css || '');
    } catch (err: any) {
      setError(err.message || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  // Scope legacy site CSS inside .package-selection-container so it cannot affect
  // font rendering (e.g. icon fonts that replace $ with a glyph) outside our widget.
  // Strip any font-family declarations to prevent icon-font hijacking.
  const safeCss = packageCss
    ? packageCss.replace(/font-family\s*:[^;]+;?/gi, '')
    : '';

  return (
    <>
      {/* Inject scoped site CSS — font-family rules stripped to prevent glyph substitution */}
      {safeCss && (
        <style dangerouslySetInnerHTML={{ __html: safeCss }} />
      )}

      <div className="package-selection-container">
        <div className="package-selection-header">
          <h1>Choose Your Package</h1>
          {onCancel && (
            <button className="back-button" onClick={onCancel}>
              ← Back
            </button>
          )}
        </div>

      <div className="package-selection-body">
        {loading && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading packages...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <p>{error}</p>
            {onCancel && (
              <button onClick={onCancel} className="btn-secondary">Go Back</button>
            )}
          </div>
        )}

        {!loading && !error && packages.length === 0 && (
          <div className="no-packages">
            <p>No packages available at this time.</p>
            {onCancel && (
              <button onClick={onCancel} className="btn-secondary">Go Back</button>
            )}
          </div>
        )}

        {!loading && !error && packages.length > 0 && (
          <div className="row">
            {packages.map((pack) => (
              <div key={pack.packageID} className="col col-md-4 d-flex justify-content-center">
                <div className="hdr">
                  <div className="card maincard">
                    <div className={pack.isPreferred === 1 ? "preferred" : "notvisible"}>
                      <h6>
                        <span className="preferredtxt">Preferred Plan</span>
                      </h6>
                    </div>

                    <div className={`card-header cardtop cardpackage${pack.isPreferred === 1 ? '-preferred' : ''}`}>
                      <div className="row">
                        <div className="col col-sm-12 text-center">
                          <span className={`cardpackage${pack.isPreferred === 1 ? '-preferred' : ''}`}>{pack.packageName}</span>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col col-sm-12 pricecontainer">
                          <span className="cardcurrency">$</span>
                          <span className="cardprice">&nbsp;{pack.packagePriceAmount.toLocaleString()}</span>
                          <span className="cardcurrency">{pack.packagePriceCents.toString().padStart(2, '0')}</span>
                        </div>
                        <div className="cardterms text-center">
                          {pack.billingTerms && <div dangerouslySetInnerHTML={{ __html: pack.billingTerms }} />}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col col-sm-12 cardselectcontainer">
                          <div className="selectcontainer">
                            <button
                              className="btn cardselect text-center align-middle"
                              onClick={() => onPackageSelect(pack.packageID)}
                            >
                              SELECT
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer cardbottom">
                      <span className="cardfeatures text-center">
                        {pack.features && <div dangerouslySetInnerHTML={{ __html: pack.features }} />}
                      </span>
                    </div>
                  </div>
                  <div className="ftr">
                    &nbsp;
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default PackageBoxes;
